import { useMemo } from 'react'
import { useSettings } from '@/context/settings-hooks'

// The theme values are arguments so every cache, compiled or manual, keys the read on them.
function readForTheme<T, A>(read: (arg: A) => T, arg: A, _theme: unknown, _version: unknown): T {
  return read(arg)
}

/**
 * Runs `read`, which reads live CSS custom properties, again whenever the app
 * theme changes. `themeVersion` bumps on every apply, so switching between two
 * same-mode custom themes/presets (which leaves `theme` unchanged) re-reads too.
 *
 * Pass a module-level `read`: an inline function is a new value on every render
 * of a component the compiler skips, so it would re-read every render. `arg` is
 * any other input the read depends on.
 */
export function useThemeTokens<T>(read: () => T): T
export function useThemeTokens<T, A>(read: (arg: A) => T, arg: A): T
export function useThemeTokens<T, A>(read: (arg?: A) => T, arg?: A): T {
  const { theme, themeVersion } = useSettings()
  return useMemo(
    () => readForTheme(read, arg, theme, themeVersion),
    [read, arg, theme, themeVersion],
  )
}
